import { usePathname, useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AUTH_STEP,
  BASE_STEPS,
  PAYMENT_STEP,
  PICKSAVED_STEP,
  StepIdType,
  StepType,
  SUMMARY_STEP,
} from "~/components/orderdraft/orderSteps";
import { AnalyticsEvents, trackEvent } from "~/connectors/analytics.connector";
import { nameValidationService } from "~/connectors/nameValidation.connector";
import { customerOrdersService } from "~/connectors/orders.connector";
import {
  ChildInfo,
  NameValidationResponse,
  Order,
  OrderFormDataDetails,
} from "~/types";


type AuthStatus = "loading" | "authenticated" | "unauthenticated";

export default function useOrderNavigation({
  authStatus,
  anonymousId,
  order,
  setOrder,
  formData,
  previousChildren,
  saveFormData,
  isLoading,
}: {
  authStatus: AuthStatus;
  anonymousId: string | null;
  order: Order | null;
  setOrder: (o: Order | null) => void;
  formData: OrderFormDataDetails;
  previousChildren: ChildInfo[];
  saveFormData: (formData: OrderFormDataDetails) => Promise<void>;
  isLoading: boolean;
}) {
  const pathname = usePathname();
  const router = useRouter();

  const [isNextLoading, setIsNextLoading] = useState(false);
  const [nameValidationResponse, setNameValidationResponse] =
    useState<NameValidationResponse | null>(null);
  const [validatingName, setValidatingName] = useState(false);

  const isStepValid = useCallback(
    (stepId: StepIdType): boolean => {
      const isNumeValid = (): boolean => {
        return (
          !!formData.childName &&
          formData.childName.trim().length > 1 &&
          !formData.childName.includes(",")
        );
      };

      const isDespreValid = (): boolean => {
        return (
          formData.age !== null &&
          formData.age !== undefined &&
          formData.age >= 0 &&
          formData.age <= 7 &&
          !!formData.gender
        );
      };

      const isPovesteValid = (): boolean => {
        return (
          (formData.estimatedCompletionPercentage ?? 0) > 20 &&
          !!formData.story &&
          formData.story.trim().length > 20
        );
      };

      const isAuthenticated = (): boolean => authStatus === "authenticated";

      switch (stepId) {
        case "picksaved":
          return true;
        case "personalizare.nume":
          return isNumeValid();
        case "personalizare.despre":
          return isNumeValid() && isDespreValid();
        case "personalizare.poveste":
          return isNumeValid() && isDespreValid() && isPovesteValid();
        case "login":
          return (
            isNumeValid() &&
            isDespreValid() &&
            isPovesteValid() &&
            isAuthenticated()
          );
        case "summary":
          return (
            isNumeValid() &&
            isDespreValid() &&
            isPovesteValid() &&
            isAuthenticated()
          );
        case "payment":
          return (
            isNumeValid() &&
            isDespreValid() &&
            isPovesteValid() &&
            isAuthenticated()
          );
        default:
          throw new Error(`Unknown step ID to validate: ${stepId}`);
      }
    },
    [formData, authStatus],
  );

  const steps = useMemo<StepType[]>(() => {
    const isAuthenticated = authStatus === "authenticated";
    const hasPreviousChildren = previousChildren.length > 0;

    if (isAuthenticated) {
      if (hasPreviousChildren)
        return [PICKSAVED_STEP, ...BASE_STEPS, SUMMARY_STEP, PAYMENT_STEP];
      return [...BASE_STEPS, SUMMARY_STEP, PAYMENT_STEP];
    }
    return [...BASE_STEPS, AUTH_STEP, SUMMARY_STEP, PAYMENT_STEP];
  }, [authStatus, previousChildren]);

  const currentStep = useMemo<StepType | null>(() => {
    return steps.find((step) => step.path === pathname) || null;
  }, [steps, pathname]);

  useEffect(() => {
    if (isLoading || !currentStep) return;

    const currentIndex = steps.findIndex((step) => step.id === currentStep.id);
    if (currentIndex === -1) return;

    for (let i = 0; i < currentIndex; i++) {
      const step = steps[i];
      if (!isStepValid(step.id)) {
        router.push(step.path);
        return;
      }
    }
  }, [currentStep, steps, isStepValid, router, isLoading]);

  const getNextStepPath = useCallback((): string | null => {
    if (!currentStep) return null;
    const currentIndex = steps.findIndex((step) => step.id === currentStep.id);
    if (currentIndex === -1) return null;
    if (currentIndex < steps.length - 1) {
      const nextStep = steps[currentIndex + 1];
      const nextStepPath = nextStep.path;

      if (nextStep.id === "summary") {
        if (anonymousId && authStatus === "unauthenticated") {
          const params = new URLSearchParams();
          params.set("orderAid", anonymousId);
          const queryString = params.toString();
          return queryString ? `${nextStepPath}?${queryString}` : nextStepPath;
        }
      }

      if (nextStep.id === "payment") {
        const params = new URLSearchParams();
        if (order) {
          params.set("orderId", order.id.toString());
          const queryString = params.toString();
          return queryString ? `${nextStepPath}?${queryString}` : nextStepPath;
        } else if (anonymousId && authStatus === "unauthenticated") {
          throw new Error(
            "Cannot proceed to payment step without an order for unauthenticated users.",
          );
        }
      }
      return nextStepPath;
    }
    return null;
  }, [currentStep, steps, order, anonymousId, authStatus]);

  const nextStep = useCallback(async (): Promise<boolean> => {
    setIsNextLoading(true);

    //if we are on auth step and we are authenticated, skip to summary
    if (pathname === AUTH_STEP.path && authStatus === "authenticated") {
      router.push(SUMMARY_STEP.path);
      setIsNextLoading(false);
      return true;
    }

    let nextStepPath = getNextStepPath();
    await saveFormData(formData);
    if (!nextStepPath) {
      setIsNextLoading(false);
      return false;
    }

    const nextStepPathname = nextStepPath.split("?")[0];
    const nextStepIndex = steps.findIndex(
      (step) => step.path === nextStepPathname,
    );

    // Name validation for "despre" step
    if (
      nextStepIndex !== -1 &&
      steps[nextStepIndex].id === "personalizare.despre"
    ) {
      const currentName = formData.childName || "";
      const wasThisValidatedBefore =
        nameValidationResponse && nameValidationResponse.name === currentName;
      const isCurrentNameInPreviousSuggestionList =
        nameValidationResponse &&
        nameValidationResponse.suggestions &&
        nameValidationResponse.suggestions.includes(currentName);

      if (!isCurrentNameInPreviousSuggestionList && !wasThisValidatedBefore) {
        let validationFailed = false;
        try {
          setValidatingName(true);
          setNameValidationResponse(null);
          const validationResult =
            await nameValidationService.validateName(currentName);
          setNameValidationResponse(validationResult);
          if (
            !validationResult.isValid &&
            validationResult.suggestions &&
            validationResult.suggestions.length > 0
          ) {
            trackEvent(AnalyticsEvents.ORDER_DRAFT_NAME_INVALID, {
              name: currentName,
              suggestionsCount: validationResult.suggestions.length,
            });
            validationFailed = true;
          } else if (validationResult.isValid) {
            trackEvent(AnalyticsEvents.ORDER_DRAFT_NAME_VALID, {
              name: currentName,
            });
          } else {
            trackEvent(AnalyticsEvents.ORDER_DRAFT_NAME_UNKNOWN, {
              name: currentName,
            });
          }
          if (validationResult.gender) {
            saveFormData({ ...formData, gender: validationResult.gender });
          }
        } catch (error) {
          console.error(
            `Error validating name ${currentName}. Will continue anyway.`,
            error,
          );
        } finally {
          setValidatingName(false);
        }
        if (validationFailed) {
          setIsNextLoading(false);
          return false;
        }
      }
    }

    // Payment step handling: create order and redirect
    if (nextStepIndex !== -1 && steps[nextStepIndex].id === "payment") {
      try {
        const orderResponse = await customerOrdersService.createOrder();
        setOrder(orderResponse);

        if (orderResponse.paymentStatus === "paid") {
          trackEvent(AnalyticsEvents.ORDER_FREE_CREATED, {
            orderId: orderResponse.id,
            amount: 0,
          });
          router.push(`/confirmare-comanda?orderId=${orderResponse.id}`);
          setIsNextLoading(false);
          return true;
        }

        trackEvent(AnalyticsEvents.ORDER_CREATED, {
          orderId: orderResponse.id,
          amount: orderResponse.total,
        });

        router.push(`/comanda/plata?orderId=${orderResponse.id}`);
        setIsNextLoading(false);
        return true;
      } catch (error) {
        console.error("Error creating order:", error);
        setIsNextLoading(false);
        return false;
      }
    }

    router.push(nextStepPath);
    setIsNextLoading(false);
    return true;
  }, [
    getNextStepPath,
    saveFormData,
    formData,
    nameValidationResponse,
    steps,
    setOrder,
    router,
    authStatus,
    pathname,
  ]);

  const prevStep = useCallback(() => {
    if (!currentStep) return;
    const currentIndex = steps.findIndex((step) => step.id === currentStep.id);
    if (currentIndex === -1) return;
    if (currentIndex > 0) {
      if (
        currentStep.id === "personalizare.nume" &&
        isStepValid("personalizare.despre")
      ) {
        router.push("/");
        return;
      }
      router.push(steps[currentIndex - 1].path);
    } else {
      router.push("/");
    }
  }, [currentStep, steps, router, isStepValid]);

  const isCurrentStepValid = useCallback(
    () => (currentStep ? isStepValid(currentStep.id) : false),
    [currentStep, isStepValid],
  );

  return {
    steps,
    currentStep,
    isNextLoading,
    nextStep,
    prevStep,
    getNextStepPath,
    isStepValid,
    isCurrentStepValid,
    nameValidationResponse,
    validatingName,
  };
}
